import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-02-18 19:10:56 +0100 (Wed, 18 Feb 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 160820 $
//  Purpose   : domain css
// =============================================================================
// ---Third-party imports
import React, { useState } from 'react';
import { Style, Link as HeadLink } from 'react-head';
// ---Framework imports
import { get, mapItems, reversed, items, sorted, flatten, strip, mergeDeep, dict } from 'ldash';
import { Actions, Sel } from 'uix/registries';
import log from 'uix/logging';
import { useSelector } from 'uix/hooks';
import { formatDistance } from 'uix/utils/date';
// ---Local imports
// ---Exports
export const concatenate = (ancestorNames, dcss)=>{
    /* concatenate domain css files over ancestor domains
   */ let collected = [];
    let totalLineCount = 0;
    let css = flatten(ancestorNames.map((dn)=>sorted(items(get(dcss, dn, []))).map(([fname, fdata])=>{
            if (!strip(fdata.text)) {
                return null;
            }
            let lineCount = fdata.text.split('\n').length;
            let name = `${dn}.${fname}.css`;
            let info = fdata.cssSourceInfo ? `From: ${fdata.cssSourceInfo}` : '';
            if (fdata.ts) {
                info += ` Age: ${formatDistance(fdata.ts)}`;
            }
            let head = `/* ${name} ${info} */`;
            collected.push([
                name,
                totalLineCount
            ]);
            totalLineCount += lineCount + 2;
            return `${head}\n${fdata.text}\n`;
        })));
    let HEAD = 4;
    let head = collected.map(([name, lineNo])=>`L${lineNo + collected.length + HEAD}: ${name}`).join('\n');
    css = css.filter((e)=>e).join('\n');
    return {
        files: collected,
        text: `/*\n${head}\n*/\n\n${css}`
    };
};
// XXX: this does not need to be in core
export const useDomainCSS = ()=>{
    let dcss = useSelector((root)=>root.config.dcss);
    let ancestors = reversed(useSelector(Sel.ancestorAndSelfDomains));
    let dname = ancestors[0].name;
    let dnames = ancestors.map((d)=>d.name);
    return {
        dcss,
        dname,
        dnames,
        ancestors
    };
};
export const mergeHotFileSystem = (dcss)=>{
    let hot_dcss = dict(mapItems(dcss, (dname, files)=>[
            dname.replace('.hotfs', ''),
            files
        ], (dname)=>dname.endsWith('.hotfs')));
    dcss = mergeDeep(dcss, hot_dcss);
    let override_dcss = dict(mapItems(dcss, (dname, files)=>[
            dname.replace('.override', ''),
            files
        ], (dname)=>dname.endsWith('.override')));
    return mergeDeep(dcss, override_dcss);
};
export const DomainStyles = ({})=>{
    /* render concatenated domain styles + favicon into html/head
   */ let mergeHotFS = useSelector((root)=>root.debug.mergeHotFS);
    let apiurl = useSelector(Sel.apiurl);
    let { dcss, dnames, dname } = useDomainCSS();
    if (mergeHotFS) {
        dcss = mergeHotFileSystem(dcss);
    }
    let { text } = concatenate(dnames, dcss);
    // some custom processing before emitting
    if (process.env.VLS_DEPLOY !== 'vls') {
        // only in hot dev server with choosable apiurl
        text = text.replace(/url\(\/domainimage\//g, `url(${apiurl}/domainimage/`);
    }
    //~ log('%s', text)
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(FavIcon, {
                dname: dname,
                apiurl: apiurl
            }),
            /*#__PURE__*/ _jsx(Style, {
                type: "text/css",
                children: text
            })
        ]
    });
};
const FavIcon = ({ dname, apiurl })=>{
    let href = apiurl.href(`/${dname}/domainimage/favicon.ico`);
    return /*#__PURE__*/ _jsx(HeadLink, {
        type: "image/x-icon",
        rel: "icon",
        href: href
    });
};
